import type { SummaryItems } from './ProfileSummaryItems.types';
import type { ReactElement, ReactNode } from 'react';

import { Flex } from '@rango-dev/whale';
import { ArrowRightLeft, ChartSpline, Layers2, Rocket, Users } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useAppSelector } from '../../../hooks/reduxHooks';
import { profileSummarySelector } from '../../../state/ProfileSlice/selectors';
import { ProfileSummaryItem } from '../ProfileSummaryItem/ProfileSummaryItem';
import { ProfileSummaryItemWithEffectiveValue } from '../ProfileSummaryItemWithEffectiveValue';

import { getItems, getItemsWithEffectiveValue } from './ProfileSummaryItems.helpers';
import styles from './ProfileSummaryItems.module.css';

const SUMMARY_LOGO_SIZE = '48px';
const SUMMARY_ICON_SIZE = '24';

export function ProfileSummaryItems(): ReactElement {
  const params = useParams<{ username: string }>();
  const { data: profileSummary, loading } = useAppSelector((state) =>
    profileSummarySelector(state, params.username),
  );
  const { t } = useTranslation();

  const items = profileSummary ? getItems(profileSummary) : null;

  const itemsWithEffectiveValue = profileSummary
    ? getItemsWithEffectiveValue(profileSummary)
    : null;

  const summaryItems: {
    [key in keyof SummaryItems]: { icon: ReactNode; title: string };
  } = {
    interactedBlockchains: {
      icon: (
        <Flex
          justify="center"
          align="center"
          width={SUMMARY_LOGO_SIZE}
          height={SUMMARY_LOGO_SIZE}
          className={`${styles.iconCircle} ${styles.interactedBlockchainsIcon}`}>
          <Layers2 size={SUMMARY_ICON_SIZE} color="var(--pink-9)" />
        </Flex>
      ),
      title: t('Interacted Chains'),
    },
    referralVolume: {
      icon: (
        <Flex
          justify="center"
          align="center"
          width={SUMMARY_LOGO_SIZE}
          height={SUMMARY_LOGO_SIZE}
          className={`${styles.iconCircle} ${styles.referralVolumeIcon}`}>
          <ArrowRightLeft size={SUMMARY_ICON_SIZE} color="var(--red-9)" />
        </Flex>
      ),
      title: t('Referral Volume'),
    },
    invitedFriends: {
      icon: (
        <Flex
          justify="center"
          align="center"
          width={SUMMARY_LOGO_SIZE}
          height={SUMMARY_LOGO_SIZE}
          className={`${styles.iconCircle} ${styles.invitedFriendsIcon}`}>
          <Users size={SUMMARY_ICON_SIZE} color="var(--blue-9)" />
        </Flex>
      ),
      title: t('Invited Friends'),
    },
    referralIncome: {
      icon: (
        <Flex
          justify="center"
          align="center"
          width={SUMMARY_LOGO_SIZE}
          height={SUMMARY_LOGO_SIZE}
          className={`${styles.iconCircle} ${styles.referralIncomeIcon}`}>
          <Rocket size={SUMMARY_ICON_SIZE} color="var(--teal-8)" />
        </Flex>
      ),
      title: t('Referral Income'),
    },
  };

  const summaryItemsWithEffectiveValue = {
    volume: {
      icon: (
        <Flex
          justify="center"
          align="center"
          width={SUMMARY_LOGO_SIZE}
          height={SUMMARY_LOGO_SIZE}
          className={`${styles.iconCircle} ${styles.volumeIcon}`}>
          <ChartSpline size={SUMMARY_ICON_SIZE} color="var(--violet-11)" />
        </Flex>
      ),
      total: {
        title: t('Total Volume'),
        description: t(
          'Total Volume represents cumulative volume of your transactions across all chains supported by Rango.',
        ),
      },
      effective: {
        title: t('Effective Volume'),
        description: t(
          'Effective Volume represents your transaction volume across the chains listed in profile and can effect on your score.',
        ),
      },
    },
    txCount: {
      icon: (
        <Flex
          justify="center"
          align="center"
          width={SUMMARY_LOGO_SIZE}
          height={SUMMARY_LOGO_SIZE}
          className={`${styles.iconCircle} ${styles.txCountIcon}`}>
          <ChartSpline size={SUMMARY_ICON_SIZE} color="var(--orange-8)" />
        </Flex>
      ),
      total: {
        title: t('Transaction Count'),
        description: t(
          "Transaction Count reflects the total count of swaps & bridges you've executed across all chains supported by Rango.",
        ),
      },
      effective: {
        title: t('Effective Transaction Count'),
        description: t(
          'Effective Transaction Count represents the total number of transactions across the chains listed in your profile and can effect on your score.',
        ),
      },
    },
  };

  return (
    <>
      {Object.keys(summaryItemsWithEffectiveValue).map((key) => {
        const itemKey = key as keyof typeof summaryItemsWithEffectiveValue;
        const item = summaryItemsWithEffectiveValue[itemKey];
        return (
          <ProfileSummaryItemWithEffectiveValue
            key={key}
            icon={item.icon}
            {...(loading || !itemsWithEffectiveValue
              ? { loading: true, total: item.total, effective: item.effective }
              : {
                  loading: false,
                  total: { ...item.total, value: itemsWithEffectiveValue[itemKey].total },
                  effective: {
                    ...item.effective,
                    value: itemsWithEffectiveValue[itemKey].effective,
                  },
                })}
          />
        );
      })}
      {Object.keys(summaryItems).map((key) => {
        const itemKey = key as keyof typeof summaryItems;
        const item = summaryItems[itemKey];
        return (
          <ProfileSummaryItem
            key={key}
            icon={item.icon}
            title={item.title}
            {...(loading || !items ? { loading: true } : { value: items[itemKey], loading: false })}
          />
        );
      })}
    </>
  );
}
